import { useEffect, useRef, useState } from "react";
import { makeBundle } from "@psplit/ledger-core";
import { repo } from "../lib/repo.js";
import { errorMessage } from "../lib/errors.js";
import { Modal } from "./Modal.js";
import { QrShow, QrScan } from "./Qr.js";

// Guided face-to-face sync. One phone shows a code, the other scans, and the
// apps decide what travels:
//  - member's "show" is a tiny "here is what I have" request (req)
//  - scanning a req answers it with ONLY the missing entries (delta)
//  - scanning a hello admits the friend, then immediately shows them the
//    group history so joining is one continuous show-scan-show-scan dance
//  - scanning a ledger imports it

type View =
  | { kind: "menu" }
  | { kind: "show"; frames: string[]; caption: string; scanReply: boolean }
  | { kind: "scan" };

export function FaceSyncModal({
  id,
  autoShow,
  onClose,
}: {
  id: string;
  autoShow?: boolean;
  onClose: () => void;
}) {
  const [view, setView] = useState<View>({ kind: "menu" });
  const [status, setStatus] = useState("");
  const membership = repo.membership(id);

  const showCode = async () => {
    setStatus("");
    if (membership === "member") {
      setView({
        kind: "show",
        frames: await repo.buildSyncRequestQrFrames(id),
        caption: "Your friend taps Scan and points their camera here. Their phone will answer with a code - scan it back.",
        scanReply: true,
      });
    } else {
      setView({
        kind: "show",
        frames: await repo.buildHelloQrFrames(id),
        caption: "A member scans this to let you in. Their phone then shows a code with the group's history - scan it back.",
        scanReply: true,
      });
    }
  };

  const started = useRef(false);
  useEffect(() => {
    if (autoShow && !started.current) {
      started.current = true;
      void showCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const onScan = async (payload: string) => {
    try {
      const result = await repo.ingestQrPayload(id, payload);
      if (result.kind === "req" && result.reqHeads) {
        const store = repo.storeOf(id);
        const theyHaveMore = result.reqHeads.some((h) => !store.has(h));
        const delta = makeBundle(store, result.reqHeads);
        if (delta.ops.length === 0) {
          setStatus(
            theyHaveMore
              ? "They have entries you don't. Tap Show code and let them scan it - their phone will send them over."
              : "You're both up to date. Nothing to send.",
          );
          setView({ kind: "menu" });
          return;
        }
        setView({
          kind: "show",
          frames: await repo.buildLedgerQrFrames(id, result.reqHeads),
          caption:
            `Sending ${delta.ops.length} entr${delta.ops.length === 1 ? "y" : "ies"} they're missing - let them scan this.` +
            (theyHaveMore ? " They also have entries you don't: afterwards, tap Show code." : ""),
          scanReply: theyHaveMore,
        });
        return;
      }
      if (result.kind === "hello") {
        if (repo.membership(id) !== "member") {
          setStatus("Only a group member can let someone in.");
          setView({ kind: "menu" });
          return;
        }
        setStatus(result.added > 0 ? "Friend admitted." : "Already a member.");
        setView({
          kind: "show",
          frames: await repo.buildLedgerQrFrames(id),
          caption: "They're in! Now let them scan this to get the group's history.",
          scanReply: false,
        });
        return;
      }
      // kind "ops"
      setStatus(
        result.added > 0
          ? `Got ${result.added} new entr${result.added === 1 ? "y" : "ies"}.${result.rejected > 0 ? ` ${result.rejected} couldn't be read.` : ""} If you added entries too, tap Show code so they can pull them.`
          : "Nothing new - you already had all of it.",
      );
      setView({ kind: "menu" });
    } catch (e) {
      setStatus(`Scan failed: ${errorMessage(e)}`);
      setView({ kind: "menu" });
    }
  };

  if (view.kind === "scan") {
    return <QrScan onPayload={(p) => void onScan(p)} onClose={() => setView({ kind: "menu" })} />;
  }

  return (
    <Modal onClose={onClose}>
      <h3>Sync in person</h3>
      {view.kind === "menu" && (
        <p className="muted">
          One phone shows a code, the other scans it. Works with no internet; everything in the
          codes is locked with the group's key.
        </p>
      )}
      {view.kind === "show" && <QrShow frames={view.frames} caption={view.caption} />}
      {status !== "" && <div className="notice">{status}</div>}
      <div className="row gap wrap">
        {view.kind === "show" ? (
          <>
            {view.scanReply && (
              <button className="btn primary" onClick={() => setView({ kind: "scan" })}>
                Scan their reply
              </button>
            )}
            <button className="btn" onClick={() => setView({ kind: "menu" })}>
              Back
            </button>
          </>
        ) : (
          <>
            <button className="btn primary" onClick={() => void showCode()}>
              {membership === "member" ? "Show code" : "Show my join code"}
            </button>
            <button className="btn" onClick={() => setView({ kind: "scan" })}>
              Scan code
            </button>
          </>
        )}
        <button className="btn" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
}
